<script lang="ts" setup>
const props = defineProps<{
  slug: string
}>()

const { locale, t } = useI18n()

const { data } = await useFetch<{
  document: (ParsedContent & { costs?: { perCompany: number, perRequest: number }, endpoints?: { DELETE?: string[], GET?: string[], POST?: string[], PUT?: string[] } })
  surround: {
    after?: ParsedContent
    before?: ParsedContent
  }
}>(`/api/server/content/${props.slug}?locale=${locale.value}&collection=api`)

useSeoMeta({
  description: data.value?.document?.description,
  title: `${data.value?.document?.titleSeo || data.value?.document?.title} - The Companies API`,
})
</script>

<template>
  <div
    v-if="data"
    class="flex flex-col"
  >
    <DocsPageHeader
      :costs="data.document.costs"
      :description="data.document.description || t('apiDocumentationDescription')"
      :endpoints="data.document.endpoints"
      :title="data.document.title || t('apiDocumentation')"
    />

    <UPageBody
      class="mt-6 max-w-none pb-0"
      prose
    >
      <ContentRenderer
        class="grid grid-cols-1 gap-6 xl:gap-8"
        :value="data.document"
      />
    </UPageBody>

    <Surround
      :surround="data.surround"
      :ui="{ wrapper: 'mt-6 gap-6 xl:mt-8' }"
    />
  </div>
</template>

<i18n lang="json">
{
  "en": {
    "apiDocumentation": "API Documentation",
    "apiDocumentationDescription": "Supercharge your product features and sales process with our API.",
    "oldVersion": {
      "action": "View the latest version",
      "description": "Some companies might be missing and our new datapoints will be unavailable when you will query this version.",
      "title": "Be careful, you are viewing an outdated version of our documentation."
    }
  },
  "es": {
    "apiDocumentation": "Documentación de la API",
    "apiDocumentationDescription": "Potencie las características de su producto y el proceso de ventas con nuestra API.",
    "oldVersion": {
      "action": "Ver la última versión",
      "description": "Algunas empresas podrían estar faltando y nuestros nuevos puntos de datos no estarán disponibles cuando consultes esta versión.",
      "title": "¡Ten cuidado, estás viendo una versión obsoleta de nuestra documentación!"
    }
  },
  "fr": {
    "apiDocumentation": "Documentation de l'API",
    "apiDocumentationDescription": "Améliorez les fonctionnalités de votre produit et votre processus de vente avec notre API.",
    "oldVersion": {
      "action": "Voir la dernière version",
      "description": "Certaines entreprises pourraient manquer et nos nouveaux datapoints ne seront pas disponibles lorsque vous interrogerez cette version.",
      "title": "Attention, vous consultez une version obsolète de notre documentation."
    }
  }
}
</i18n>
